import { ensureCustomFilterStyle } from './filterStyles';

/**
 * 금액 범위 필터 (만원 단위)
 * 매매가, 투자금 등에 사용
 * 사용 예: { filter: MoneyRangeFilter }
 */
class MoneyRangeFilter {
  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.model = null;
    this.onClose = params.onClose || null;
    this.gui = document.createElement('div');
    this.gui.className = 'aptrank-custom-filter';
    this.render();
  }

  render() {
    const maxLimit = 200000;
    const minValue = Number.isFinite(this.model?.min) ? this.model.min : '';
    const maxValue = Number.isFinite(this.model?.max) && this.model.max !== Number.POSITIVE_INFINITY ? this.model.max : '';
    // 설정값이 없으면(빈 문자열) 슬라이더는 전체 범위(min=좌측끝, max=우측끝)로 배치해 두 핸들이 겹치지 않도록 한다.
    // 주의: Number('')는 0(유한값)이라 단순 isFinite 검사로는 빈 값 판별이 안 됨 → 빈 문자열을 명시적으로 처리.
    const sliderMin = minValue === '' ? 0 : Math.max(0, Math.min(maxLimit, Number(minValue) || 0));
    const sliderMax = maxValue === '' ? maxLimit : Math.max(0, Math.min(maxLimit, Number(maxValue) || maxLimit));

    const formatKoreanPrice = (value) => {
      if (!value) return '';
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      const billion = Math.floor(num / 10000);
      const million = Math.floor((num % 10000) / 1000);
      if (billion > 0 && million > 0) {
        return `${billion}억 ${million * 1000}만원`;
      } else if (billion > 0) {
        return `${billion}억원`;
      } else {
        return `${num}만원`;
      }
    };

    this.gui.innerHTML = `
      <button type="button" class="complex-top-filter-panel-close" style="position: absolute; top: 8px; right: 8px; border: none; background: transparent; color: #64748b; width: 28px; height: 28px; border-radius: 8px; font-size: 24px; line-height: 1; cursor: pointer; z-index: 10;">×</button>
      <div class="aptrank-custom-filter-head">
        <div class="aptrank-custom-filter-heading">금액 범위 (만원)</div>
      </div>
      <div class="aptrank-custom-filter-title-edit" style="align-items: flex-start;">
        <div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;">
          <div class="korean-price-label min-korean" style="font-size: 10px; color: #64748b; height: 14px; line-height: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${formatKoreanPrice(minValue)}</div>
          <input class="title-min" inputmode="numeric" placeholder="최소" value="${minValue}" />
        </div>
        <span style="margin-top: 16px; flex-shrink: 0;">~</span>
        <div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;">
          <div class="korean-price-label max-korean" style="font-size: 10px; color: #64748b; height: 14px; line-height: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${formatKoreanPrice(maxValue)}</div>
          <input class="title-max" inputmode="numeric" placeholder="최대" value="${maxValue}" />
        </div>
      </div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider min-slider" type="range" min="0" max="${maxLimit}" step="1000" value="${Math.max(0, Math.min(maxLimit, sliderMin))}" />
        <input class="aptrank-custom-filter-slider max-slider" type="range" min="0" max="${maxLimit}" step="1000" value="${Math.max(0, Math.min(maxLimit, sliderMax))}" />
      </div>
      <div class="aptrank-custom-filter-grid">
        <button class="aptrank-custom-filter-chip" data-range="0-10000">~1억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-20000">~2억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-30000">~3억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-40000">~4억</button>
        <button class="aptrank-custom-filter-chip" data-range="0-50000">~5억</button>
        <button class="aptrank-custom-filter-chip" data-range="50000-100000">5~10억</button>
        <button class="aptrank-custom-filter-chip" data-range="1000000-999999999">100억~</button>
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    const updateSliders = () => {
      const minInput = this.gui.querySelector('input.title-min');
      const maxInput = this.gui.querySelector('input.title-max');
      const minSlider = this.gui.querySelector('input.min-slider');
      const maxSlider = this.gui.querySelector('input.max-slider');

      const minVal = minInput.value.replace(/[^\d]/g, '');
      const maxVal = maxInput.value.replace(/[^\d]/g, '');

      if (minVal) {
        const minNum = Math.min(Number(minVal), maxLimit);
        minSlider.value = minNum;
      }
      if (maxVal) {
        const maxNum = Math.min(Number(maxVal), maxLimit);
        maxSlider.value = maxNum;
      }
    };

    const applyFilter = () => {
      const minRaw = String(this.gui.querySelector('input.title-min').value || '').replace(/[^\d]/g, '');
      const maxRaw = String(this.gui.querySelector('input.title-max').value || '').replace(/[^\d]/g, '');
      const min = minRaw === '' ? null : Number(minRaw);
      const max = maxRaw === '' ? null : Number(maxRaw);

      if (!Number.isFinite(min) && !Number.isFinite(max)) {
        this.model = null;
      } else {
        this.model = {
          min: Number.isFinite(min) ? min : 0,
          max: Number.isFinite(max) ? max : Number.POSITIVE_INFINITY,
        };
      }
      this.params.filterChangedCallback();
    };

    const updateKoreanLabels = () => {
      const minInput = this.gui.querySelector('input.title-min');
      const maxInput = this.gui.querySelector('input.title-max');
      const minKorean = this.gui.querySelector('.min-korean');
      const maxKorean = this.gui.querySelector('.max-korean');

      if (minKorean) {
        minKorean.textContent = formatKoreanPrice(minInput.value);
      }
      if (maxKorean) {
        maxKorean.textContent = formatKoreanPrice(maxInput.value);
      }
    };

    const updateTrack = () => {
      const minSlider = this.gui.querySelector('input.min-slider');
      const maxSlider = this.gui.querySelector('input.max-slider');
      const track = this.gui.querySelector('.aptrank-custom-filter-slider-track');
      if (!track || !minSlider || !maxSlider) return;

      const minVal = Number(minSlider.value);
      const maxVal = Number(maxSlider.value);
      const leftPercent = (minVal / maxLimit) * 100;
      const rightPercent = 100 - (maxVal / maxLimit) * 100;

      track.style.left = `${leftPercent}%`;
      track.style.right = `${rightPercent}%`;
    };

    // 슬라이더 변경 시 입력 필드 업데이트 및 트랙 업데이트 (적용은 버튼으로만)
    this.gui.querySelector('input.min-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const maxText = this.gui.querySelector('input.title-max').value.replace(/[^\d]/g, '');
      const max = maxText ? Number(maxText) : maxLimit;
      this.gui.querySelector('input.title-min').value = String(Math.min(next, max));
      updateKoreanLabels();
      updateTrack();
    });

    this.gui.querySelector('input.max-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const minText = this.gui.querySelector('input.title-min').value.replace(/[^\d]/g, '');
      const min = minText ? Number(minText) : 0;
      this.gui.querySelector('input.title-max').value = String(Math.max(next, min));
      updateKoreanLabels();
      updateTrack();
    });

    // 초기 트랙 위치 설정
    updateTrack();

    // 입력 필드 변경 시 슬라이더 및 트랙 업데이트 (적용은 버튼으로만)
    this.gui.querySelector('input.title-min').addEventListener('input', () => {
      updateSliders();
      updateKoreanLabels();
      updateTrack();
    });
    this.gui.querySelector('input.title-max').addEventListener('input', () => {
      updateSliders();
      updateKoreanLabels();
      updateTrack();
    });

    // 버튼 클릭 시 값만 설정 (적용 버튼 눌러야 적용됨)
    this.gui.querySelectorAll('[data-range]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const [minStr, maxStr] = String(btn.getAttribute('data-range') || '').split('-');
        const min = Number(minStr || 0);
        const max = Number(maxStr || 999999999);
        this.gui.querySelector('input.title-min').value = min;
        this.gui.querySelector('input.title-max').value = max;
        updateSliders();
        updateKoreanLabels();
        updateTrack();
      });
    });

    // 초기화 버튼 (닫히지 않음)
    this.gui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.gui.querySelector('input.title-min').value = '';
      this.gui.querySelector('input.title-max').value = '';
      this.gui.querySelector('input.min-slider').value = 0;
      this.gui.querySelector('input.max-slider').value = maxLimit;
      updateTrack();
      this.model = null;
      this.params.filterChangedCallback();
    });

    // 적용 버튼
    this.gui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      applyFilter();
    });

    // 닫기 버튼
    const closeBtn = this.gui.querySelector('.complex-top-filter-panel-close');
    if (closeBtn && this.onClose) {
      closeBtn.addEventListener('click', this.onClose);
    }
  }

  getGui() {
    return this.gui;
  }

  isFilterActive() {
    return !!this.model;
  }

  doesFilterPass(p) {
    if (!this.model) return true;
    const raw = this.params.valueGetter ? this.params.valueGetter(p.node) : p?.data?.[this.params.colDef?.field];
    const value = Number(raw);
    if (!Number.isFinite(value)) return false;
    return value >= this.model.min && value <= this.model.max;
  }

  getModel() {
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (this.gui) this.render();
  }
}

export default MoneyRangeFilter;
